/**
 * DashboardSkeleton — lightweight shimmer placeholders
 * Used while API data loads to prevent blank screens
 */
import React from 'react';

const pulse = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'db-shimmer 1.4s ease-in-out infinite',
  borderRadius: 8,
};

export function SkeletonBox({ w = '100%', h = 20, radius = 8, style = {} }) {
  return <div style={{ ...pulse, width: w, height: h, borderRadius: radius, flexShrink: 0, ...style }} />;
}

export function SkeletonCard({ rows = 3, style = {} }) {
  return (
    <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,.06)', ...style }}>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonBox key={i} h={i === 0 ? 18 : 14} w={i === 0 ? '60%' : `${70 + Math.random()*25}%`} style={{ marginBottom: i < rows-1 ? 12 : 0 }} />
      ))}
    </div>
  );
}

export function SkeletonStatGrid({ cols = 4 }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols},1fr)`, gap:16, marginBottom:24 }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
          <SkeletonBox w={32} h={32} radius={8} style={{ marginBottom:12 }} />
          <SkeletonBox h={14} w="50%" style={{ marginBottom:8 }} />
          <SkeletonBox h={28} w="70%" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonSessionList({ rows = 3 }) {
  return (
    <div style={{ background:'#fff', borderRadius:12, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
      <SkeletonBox h={18} w="40%" style={{ marginBottom:20 }} />
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom: i < rows-1 ? '1px solid #f3f4f6' : 'none' }}>
          <SkeletonBox w={40} h={40} radius={10} style={{ flexShrink:0 }} />
          <div style={{ flex:1 }}>
            <SkeletonBox h={14} w="55%" style={{ marginBottom:8 }} />
            <SkeletonBox h={12} w="35%" />
          </div>
          <SkeletonBox w={80} h={32} radius={8} />
        </div>
      ))}
    </div>
  );
}

export default function DashboardSkeleton({ cols = 4 }) {
  return (
    <div style={{ padding:'0 4px' }}>
      <style>{`@keyframes db-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      <SkeletonBox h={36} w="45%" style={{ marginBottom:8 }} />
      <SkeletonBox h={16} w="60%" style={{ marginBottom:28 }} />
      <SkeletonStatGrid cols={cols} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <SkeletonSessionList rows={3} />
        <SkeletonCard rows={4} style={{ padding:24 }} />
      </div>
    </div>
  );
}
